<template>
  <div :class="getCardSize()">
    <div :class="getCardVariant()">
      <div class="card-header">
        <slot name="header"></slot>
        <div class="d-flex justify-content-between align-items-center">
          <slot name="header-title">
            <div class="d-flex flex-column">
              <strong>{{ title }}</strong>
              <slot name="header-subtitle"></slot>
            </div>
          </slot>
          <slot name="header-action"></slot>
        </div>
      </div>
      <div class="card-body">
        <slot name="body-content">
          <ul class="list-group">
            <draggable
              :id="id"
              :group="group"
              :sort="false"
              @end="onMoveOrder"
              draggable=".list-group-item"
            >
              <li
                v-for="item in items"
                :key="item.id"
                :id="`order-${item.id}`"
                @click="onClickOrder(item)"
                class="list-group-item"
              >
                <div class="card no-border mb-0">
                  <div class="card-text">
                    <table class="w-100">
                      <tr>
                        <td>
                          <b-row>
                            <b-col>
                              <strong>{{ getOrderNumber(item) }}</strong>
                            </b-col>
                            <b-col v-if="getCrew(item)">
                              <small class="crew-right">
                                <b-badge variant="secondary">{{ getCrew(item) }}</b-badge>
                              </small>
                            </b-col>
                          </b-row>
                          <b-row>
                            <b-col>
                              <b-badge v-if="showExtra(item)" variant="danger">
                                <i>{{ getExtra(item) }}</i>
                              </b-badge>
                            </b-col>
                          </b-row>
                        </td>
                      </tr>
                      <tr>
                        <td class="pr-5">
                          <strong>{{ getAssetDescription(item) }}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <b-row>
                            <b-col lg="8" md="8">
                              <span>{{ getOrderDescription(item) }}</span>
                            </b-col>
                            <b-col lg="4" md="4">
                              <i
                                class="job-duration"
                                :class="{
                                  'yellow-duration': getClassByDurationJob(item, 7, 14) && !isCompleted,
                                  'red-duration': getClassByDurationJob(item, 14) && !isCompleted
                                  }"
                                >
                                <b>{{ getHumanizeDateJob(item) }}</b>
                              </i>
                            </b-col>
                          </b-row>
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              </li>
            </draggable>
          </ul>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import get from 'lodash/get';
import moment from 'moment';

export default {
  components: {
    draggable,
  },
  props: {
    id: {
      type: String,
      required: false,
    },
    title: {
      type: String,
      required: true,
    },
    group: {
      type: Object,
      required: false,
    },
    variant: {
      type: String,
      required: false,
    },
    items: {
      type: Array,
      required: true,
    },
    lg: {
      type: Number,
      required: false,
    },
    md: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      extraType: [
        'Partially Completed',
        'With Exception',
      ],
    };
  },
  computed: {
    boardType() {
      if (this.id) {
        const splitId = this.id.split('-');
        return splitId[0];
      }
      return null;
    },
    isRejected() {
      return this.boardType === 'rejected';
    },
    isCompleted() {
      return this.boardType === 'completed';
    },
  },
  methods: {
    getCardSize() {
      const lg = this.lg ? this.lg : 3;
      const md = this.md ? this.md : 12;
      return `col-lg-${lg} col-md-${md}`;
    },
    getCardVariant() {
      const cardVariant = ['card'];
      if (this.variant) cardVariant.push(`bg-${this.variant} text-dark`);
      return cardVariant.join(' ');
    },
    getCrew(item) {
      const crew = get(item, 'crew', null);
      return crew;
    },
    getAssetDescription(order) {
      let addressLine1 = get(order, 'destination.metadata.address_line_1', '');
      const addressLine2 = get(order, 'destination.metadata.address_line_2', '');
      if (addressLine1 !== '' && addressLine2 !== '') {
        addressLine1 += ` ${addressLine2}`;
      }
      const assetDescription = get(order, 'destination.name', '');
      return addressLine1 || assetDescription;
    },
    getOrderNumber(order) {
      const orderNumber = get(order, 'number', '');
      return orderNumber;
    },
    getOrderDescription(order) {
      const description = get(order, 'metadata.work_order_description', '');
      return description;
    },
    onMoveOrder(...args) {
      this.$emit('onMoveOrder', ...args);
    },
    onClickOrder(order) {
      this.$emit('onClickOrder', order);
    },
    getHumanizeDateJob(order) {
      const created_at = get(order, 'created_at', '');
      const diffDate = moment().diff(moment(created_at));
      const diffDays = moment.duration(diffDate).asDays();
      return diffDays < 1
        ? moment(created_at).fromNow()
        : `${Math.floor(diffDays)} days ago`;
    },
    getClassByDurationJob(order, compareDate, maxDate) {
      const created_at = get(order, 'created_at', '');
      const diffDate = moment().diff(moment(created_at));
      const diffDays = moment.duration(diffDate).asDays();
      return (diffDays >= compareDate) && (maxDate ? diffDays < maxDate : true);
    },
    showExtra(order) {
      const extraType = get(order, 'metadata.status.extra.type', false);
      const isExtra = extraType !== false;
      return isExtra &&
        this.extraType[parseInt(extraType, 10)] &&
        (this.isRejected || this.isCompleted);
    },
    getExtra(order) {
      const extraType = get(order, 'metadata.status.extra.type', 0);
      return this.extraType[parseInt(extraType, 10)];
    },
  },
};
</script>

<style scoped>
.card-header {
  background-color: #fff !important;
}
.card-body > .col-lg-12 {
  padding: 0px;
  margin-bottom: 8px;
}
.card-body > .col-lg-12 > .card {
  margin-bottom: 0px;
  border-radius: 5px;
}
.col-lg-3 > .card {
  border-radius: 5px;
}
.col-lg-3 > .card > .card-header {
  border-top-left-radius: 5px;
  border-top-right-radius: 5px
}
.list-group {
  border-bottom: none;
}
.card-body .list-group>div:empty::after {
    content: 'There\'s No Order';
    display: block;
    margin-bottom: 1.1rem;
    width: 100%;
    text-align: center;
    margin-top: 0.7rem;
}
.list-group-item {
  background: white;
  min-height: 3.2em;
  border: 1px solid #e2e2e2;
  margin-bottom: 8px;
  box-shadow: 0px 2px 7px 0px rgb(50 50 50 / 15%);
  touch-action: none;
  cursor: pointer;
  border-radius: 5px;
}
.crew-right {
  float: right;
  font-size: 14px;
}

.job-duration {
  font-size: 11px;
  padding: 0px 5px;
  position: absolute;
  bottom: 0px;
  right: 5px;
}

.yellow-duration {
  background-color: yellow;
}

.red-duration {
  background-color: red;
  color: white;
}

@media (max-width: 1270px) {
  .crew-right {
    float: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/BoardCard.vue